import {
  DeliveryDispatchStatus,
  DeliveryProvider,
  LegacyOrderMethod,
  OrderFulfillmentStatus,
  OrderMethod,
  PickupTimeOptimizationStrategy,
} from '../exports/Constants';

class OrderHelpers {
  isDelivery = (order) => order.method === OrderMethod.Delivery;

  isExternalDelivery = (order) => this.isDelivery(order) && order.delivery_provider !== DeliveryProvider.Standalone;

  // Table ordering. Accepts the pre-rename value too until PROD-829.
  isOnSite = (order) => order?.method === OrderMethod.OnSite || order?.method === LegacyOrderMethod.RoomService;

  // The method to key copy and per-method maps on: the legacy on-site value reads as OrderMethod.OnSite.
  canonicalMethod = (order) => (this.isOnSite(order) ? OrderMethod.OnSite : order?.method);

  // The method to build per-method copy keys from (`time_tag.${method}`, `buttons.confirm_${method}`):
  // a method this build does not know reads as pickup, the app's copy for anything not delivered,
  // instead of a raw i18n key.
  copyMethod = (order) => {
    const method = this.canonicalMethod(order);

    return Object.values(OrderMethod).includes(method) ? method : OrderMethod.Pickup;
  };

  // --- Badge / card states (see OrderCard) ---

  isPendingConfirmation = (order) => !order.is_confirmed;

  // A failed dispatch is not pending any more: the restaurant delivers it itself (or cancels).
  isPendingDriver = (order) =>
    order.is_confirmed &&
    this.isExternalDelivery(order) &&
    !order.is_driver_assigned &&
    !this.isDeliveryDispatchFailed(order);

  isDeliveryDispatchRetrying = (order) => order.delivery_dispatch_status === DeliveryDispatchStatus.Retrying;

  isDeliveryDispatchFailed = (order) => order.delivery_dispatch_status === DeliveryDispatchStatus.Failed;

  isScheduledOrderWithPendingPreparation = (order) =>
    !!order.scheduled_for &&
    (order.fulfillment_status === OrderFulfillmentStatus.Created ||
      order.fulfillment_status === OrderFulfillmentStatus.Reviewed ||
      order.fulfillment_status === OrderFulfillmentStatus.Accepted);

  isRejected = (order) => order.fulfillment_status === OrderFulfillmentStatus.Rejected;

  isComplete = (order) => !this.isRejected(order) && order.fulfillment_status === OrderFulfillmentStatus.Picked;

  isOngoing = (order) =>
    !this.isPendingConfirmation(order) &&
    !this.isPendingDriver(order) &&
    !this.isScheduledOrderWithPendingPreparation(order) &&
    !this.isComplete(order) &&
    !this.isRejected(order);

  // --- Work-queue categorization (see OngoingOrdersProvider) ---

  isActive = (order) =>
    order.fulfillment_status !== OrderFulfillmentStatus.OnDelivery &&
    order.fulfillment_status !== OrderFulfillmentStatus.Picked &&
    order.fulfillment_status !== OrderFulfillmentStatus.Rejected;

  isScheduled = (order) => order.fulfillment_status === OrderFulfillmentStatus.Accepted && order.scheduled_for !== null;

  isFutureOrder = (order) =>
    // Scheduled order, preparation time is later
    this.isScheduled(order) ||
    // External delivery with pending driver
    (this.isExternalDelivery(order) && !order.is_driver_assignment_handled);

  isPending = (order) => !order.is_manually_confirmed;

  // --- Completion (see OngoingOrdersProvider.markOrderComplete) ---

  getCompletionFulfillmentStatus = (order) =>
    order.method === OrderMethod.Delivery && order.delivery_provider === DeliveryProvider.FirstDelivery
      ? OrderFulfillmentStatus.OnDelivery
      : OrderFulfillmentStatus.Picked;

  // --- Pickup-time optimization / receipt printing (see CompanyChannel, OrderPreparationReminderScreen) ---

  getPickupTimeOptimizationStrategy = (order, company) =>
    (company?.pickup_time_optimization_strategies ?? {})[order.delivery_provider] ?? null;

  // External delivery whose receipt is printed by the manual driver-assignment confirmation
  // (OrderPreparationReminderScreen), NOT by the pickup-time-optimized Pusher event. Used to
  // suppress the channel auto-print so these orders are not printed twice.
  isPrintDeferredToDriverAssignment = (order, company) =>
    this.isExternalDelivery(order) &&
    this.getPickupTimeOptimizationStrategy(order, company) === PickupTimeOptimizationStrategy.WaitForDriverAssignment;

  // --- Minimum preparation time (see PendingOrderAlertScreen) ---

  getMinPreparationTime = (order, company) => {
    if (!this.isExternalDelivery(order)) {
      return null;
    }

    const fromCompany = company?.default_delivery_prep_times?.[order.delivery_provider];

    if (fromCompany) {
      return fromCompany;
    }

    if (order.delivery_provider === DeliveryProvider.WoltDrive) {
      return 15;
    }

    if (order.delivery_provider === DeliveryProvider.UberDrive) {
      return 20;
    }

    return 30;
  };
}

export default new OrderHelpers();
